import type { Ref } from "react";
import { cn } from "@/lib/utils";

type PageHeadlineLevel = "h1" | "h2";

const HEADING_TAG = {
  h1: "h1",
  h2: "h2",
} as const;

interface PageHeadlineProps {
  title: string;
  level: PageHeadlineLevel;
  /** Extra heading classes, such as the first-lesson h1 visual size. */
  headingClassName?: string;
  /** When set, the heading can take focus after a section unveil. */
  headingRef?: Ref<HTMLHeadingElement | null>;
  className?: string;
}

/**
 * Short brand gradient under the page title.
 */
const AccentRule = () => (
  <div
    aria-hidden="true"
    className="h-1 w-24 rounded-full bg-linear-to-r from-primary to-secondary"
  />
);

/**
 * Page headline — lesson or section title with the accent that marks it
 * above content headings.
 */
const PageHeadline = ({
  title,
  level,
  headingClassName,
  headingRef,
  className,
}: PageHeadlineProps) => {
  const Heading = HEADING_TAG[level];
  // Used when the chapter Abschluss unveils, so focus lands on this title.
  const headingIsFocusTarget = headingRef != null;

  return (
    <div className={className}>
      <Heading
        ref={headingRef}
        tabIndex={headingIsFocusTarget ? -1 : undefined}
        className={cn(
          "mb-3",
          headingIsFocusTarget && "outline-none",
          headingClassName
        )}
      >
        {title}
      </Heading>
      <AccentRule />
    </div>
  );
};

export default PageHeadline;
